/**
 * Questionnaire Results modal — the facilitation side of the pre-workshop
 * questionnaire, launched from the Asset Locker (gated by
 * `can(user, "view:questionnaire-results")`, mirroring the BFF's read
 * grants on the `questionnaires` tier).
 *
 * Reads every submission for the workshop via
 * `useQuestionnaireResponses.loadAll()` (prefix list + fan-out GET in BFF
 * mode; IDB-only locally) and renders:
 *
 *   - KPI tiles (`.dt-kpi-*`) — responses, completion, roles, avg score.
 *   - Per-question distributions — choice + scale answers tallied into the
 *     app's CSS bar idiom (`.dt-bar-*`), free text listed verbatim with an
 *     optional AI "Group similar answers" pass (see `clustering.ts`).
 *   - Breakdown of users — responses-by-role bars + a respondent table,
 *     each row expandable to that person's full answers.
 *
 * Aggregation is a `useMemo` over the store snapshot (the
 * `useProgramRollup` contract: derived counts are never persisted).
 * Questions can drift after submissions exist (power-user overrides), so
 * tallies key by question id against the RESOLVED current questionnaire and
 * orphaned answers surface under "(question removed)" instead of vanishing.
 *
 * Chrome matches the sibling Asset Locker output modals: `PanelDialogContent`
 * + `.modal-shell` / `.modal-header` / `.modal-body > .dt-pane` /
 * `.modal-footer`, sr-only DialogTitle/Description (CapturesModal is the
 * template).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChartBar,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { modalContentByWorkshop } from "@/features/workshop/modals/modal-content";
import type {
  QuestionnaireContent,
  QuestionnaireQuestion,
} from "@/features/workshop/modals/modal-content";
import { resolveField } from "@/features/workshop/overrides/workshop-override-content";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import {
  useQuestionnaireResponses,
  type QuestionnaireResponse,
} from "@/stores/useQuestionnaireResponses";
import {
  clusterAnswers,
  isClusteringAvailable,
  type AnswerTheme,
} from "@/features/xaes-analyst/clustering";
import type { Workshop } from "@/content/workshops";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
};

const EMPTY_RESPONSES: readonly QuestionnaireResponse[] = Object.freeze([]);

/* ───────────────────────── Aggregation ───────────────────────── */

type ChoiceTally = {
  kind: "choice";
  question: QuestionnaireQuestion;
  rows: { label: string; count: number }[];
  answered: number;
};

type ScaleTally = {
  kind: "scale";
  question: QuestionnaireQuestion;
  rows: { label: string; count: number }[];
  answered: number;
  average: number | null;
};

type TextTally = {
  kind: "text";
  question: QuestionnaireQuestion;
  answers: { text: string; by: string }[];
};

type QuestionTally = ChoiceTally | ScaleTally | TextTally;

type Aggregation = {
  total: number;
  /** Mean per-response completion across the current question set, 0–100. */
  completionPct: number;
  /** Distinct role → response count, sorted by count desc. */
  roleRows: { label: string; count: number }[];
  questions: QuestionTally[];
  /** Answers whose question id no longer exists in the resolved set. */
  orphaned: { questionId: string; count: number }[];
};

function scaleSteps(q: QuestionnaireQuestion): number[] {
  const min = q.min ?? 1;
  const max = q.max ?? 5;
  if (max <= min) return [min];
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}

function aggregate(
  questionnaire: QuestionnaireContent,
  responses: readonly QuestionnaireResponse[],
): Aggregation {
  const questions = questionnaire.questions;
  const knownIds = new Set(questions.map((q) => q.id));

  const total = responses.length;
  const completionPct =
    total === 0 || questions.length === 0
      ? 0
      : Math.round(
          (responses.reduce(
            (sum, r) =>
              sum +
              questions.filter((q) => {
                const v = r.answers[q.id];
                return v !== undefined && String(v).trim().length > 0;
              }).length /
                questions.length,
            0,
          ) /
            total) *
            100,
        );

  const roleCounts = new Map<string, number>();
  for (const r of responses) {
    const role = r.role || "Unspecified";
    roleCounts.set(role, (roleCounts.get(role) ?? 0) + 1);
  }
  const roleRows = [...roleCounts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);

  const tallies: QuestionTally[] = questions.map((q) => {
    const values = responses
      .map((r) => ({ value: r.answers[q.id], by: r.displayName }))
      .filter(
        (v): v is { value: string | number; by: string } =>
          v.value !== undefined && String(v.value).trim().length > 0,
      );

    if (q.type === "choice") {
      const options = q.options ?? [];
      const counts = new Map<string, number>(options.map((o) => [o, 0]));
      for (const { value } of values) {
        const key = String(value);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      // Authored options first (stable order), then any stray values
      // (submitted before an option was renamed/removed).
      const rows = [
        ...options.map((o) => ({ label: o, count: counts.get(o) ?? 0 })),
        ...[...counts.entries()]
          .filter(([label]) => !options.includes(label))
          .map(([label, count]) => ({ label, count })),
      ];
      return { kind: "choice", question: q, rows, answered: values.length };
    }

    if (q.type === "scale") {
      const steps = scaleSteps(q);
      const counts = new Map<number, number>(steps.map((s) => [s, 0]));
      let sum = 0;
      let numeric = 0;
      for (const { value } of values) {
        const n = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(n)) continue;
        numeric += 1;
        sum += n;
        if (counts.has(n)) counts.set(n, (counts.get(n) ?? 0) + 1);
      }
      return {
        kind: "scale",
        question: q,
        rows: steps.map((s) => ({ label: String(s), count: counts.get(s) ?? 0 })),
        answered: values.length,
        average: numeric > 0 ? Math.round((sum / numeric) * 10) / 10 : null,
      };
    }

    return {
      kind: "text",
      question: q,
      answers: values.map((v) => ({ text: String(v.value), by: v.by })),
    };
  });

  const orphanCounts = new Map<string, number>();
  for (const r of responses) {
    for (const qid of Object.keys(r.answers)) {
      if (!knownIds.has(qid)) {
        orphanCounts.set(qid, (orphanCounts.get(qid) ?? 0) + 1);
      }
    }
  }
  const orphaned = [...orphanCounts.entries()].map(([questionId, count]) => ({
    questionId,
    count,
  }));

  return { total, completionPct, roleRows, questions: tallies, orphaned };
}

/* ───────────────────────── Modal ───────────────────────── */

export function QuestionnaireResultsModal({
  open,
  onOpenChange,
  workshopId,
}: Props) {
  const wsId = workshopId as Workshop["id"];
  const override = useEffectiveWorkshopOverride(workshopId);
  const content = modalContentByWorkshop[wsId];

  const hydrate = useQuestionnaireResponses((s) => s.hydrate);
  const loadAll = useQuestionnaireResponses((s) => s.loadAll);
  const loadState = useQuestionnaireResponses((s) => s.loadState);
  const loadError = useQuestionnaireResponses((s) => s.loadError);
  const responses = useQuestionnaireResponses(
    (s) =>
      s.entriesByWorkshop[wsId] ??
      (EMPTY_RESPONSES as QuestionnaireResponse[]),
  );

  useEffect(() => {
    if (!open) return;
    void hydrate().then(() => loadAll(wsId));
  }, [open, hydrate, loadAll, wsId]);

  const questionnaire = content
    ? resolveField(override, "questionnaire", content.questionnaire)
    : null;

  const agg = useMemo(
    () =>
      questionnaire
        ? aggregate(questionnaire, responses)
        : null,
    [questionnaire, responses],
  );

  if (!questionnaire || !agg) return null;

  const scaleAvg = agg.questions.find(
    (t): t is ScaleTally => t.kind === "scale" && t.average !== null,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent className="max-w-6xl border-0 bg-transparent p-0 shadow-none">
        <DialogTitle className="sr-only">
          Questionnaire Results — {workshopId}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Aggregated pre-workshop questionnaire responses for {workshopId}.
        </DialogDescription>
        <div className="modal-shell" style={{ maxHeight: "85vh" }}>
          {/* HEADER */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ChartBar
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-gold)" }}
              />
              <span className="modal-title">QUESTIONNAIRE RESULTS</span>
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-gold-rgb), 0.08)",
                  border: "1px solid rgba(var(--accent-gold-rgb), 0.25)",
                  borderRadius: 10,
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-gold)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                ASSET LOCKER — {workshopId}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 6,
                }}
              >
                {agg.total} {agg.total === 1 ? "response" : "responses"}
              </span>
              <button
                type="button"
                onClick={() => void loadAll(wsId)}
                disabled={loadState === "loading"}
                className="modal-close-btn"
                aria-label="Refresh responses"
                title="Refresh responses"
              >
                <RefreshCw
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    animation:
                      loadState === "loading" ? "spin 1.1s linear infinite" : undefined,
                  }}
                />
                REFRESH
              </button>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              {loadState === "error" && (
                <p
                  role="alert"
                  className="pb-3 text-xs"
                  style={{ color: "var(--accent-danger)" }}
                >
                  Couldn't load responses from the server — showing what's
                  cached on this device. ({loadError})
                </p>
              )}

              {agg.total === 0 ? (
                <EmptyState loading={loadState === "loading"} />
              ) : (
                <>
                  {/* KPI tiles */}
                  <div className="dt-kpi-grid">
                    <Kpi
                      label="Responses"
                      value={String(agg.total)}
                      sub="submitted questionnaires"
                      color="var(--accent-gold)"
                    />
                    <Kpi
                      label="Completion"
                      value={`${agg.completionPct}%`}
                      sub="questions answered per response"
                      color="var(--accent-green)"
                    />
                    <Kpi
                      label="Roles"
                      value={String(agg.roleRows.length)}
                      sub="distinct respondent roles"
                      color="var(--accent-true-blue)"
                    />
                    <Kpi
                      label="Avg readiness"
                      value={
                        scaleAvg?.average != null ? String(scaleAvg.average) : "—"
                      }
                      sub={
                        scaleAvg
                          ? truncate(scaleAvg.question.label, 40)
                          : "no scale question"
                      }
                      color="var(--accent-sky)"
                    />
                  </div>

                  {/* Per-question distributions */}
                  {agg.questions.map((tally, i) => (
                    <QuestionSection
                      key={tally.question.id}
                      index={i}
                      tally={tally}
                      total={agg.total}
                    />
                  ))}

                  {agg.orphaned.length > 0 && (
                    <div
                      className="pt-3 text-xs"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {agg.orphaned.map((o) => (
                        <div key={o.questionId}>
                          (question removed) — id "{o.questionId}", answered in{" "}
                          {o.count} {o.count === 1 ? "response" : "responses"}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Breakdown of users */}
                  <div className="dt-section-title" style={{ marginTop: 16 }}>
                    Respondent Breakdown
                  </div>
                  <BarList
                    rows={agg.roleRows}
                    total={agg.total}
                    color="var(--accent-true-blue)"
                  />
                  <RespondentTable
                    responses={responses}
                    questionnaire={questionnaire}
                  />
                </>
              )}
            </div>
          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">
              Asset Locker — Questionnaire Results
            </span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: "var(--accent-gold)" }}
            >
              {workshopId}
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Sections ───────────────────────── */

function EmptyState({ loading }: { loading: boolean }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 py-12"
      style={{ color: "var(--text-muted)" }}
    >
      <ChartBar aria-hidden style={{ width: 22, height: 22, opacity: 0.5 }} />
      <span
        style={{
          fontSize: "var(--fs-label)",
          letterSpacing: 2,
          textTransform: "uppercase",
        }}
      >
        {loading ? "Loading responses…" : "No responses submitted yet"}
      </span>
      {!loading && (
        <span className="text-xs">
          Participants submit from the Brief panel's Questionnaire.
        </span>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div className="dt-kpi-card">
      <div className="dt-kpi-label">{label}</div>
      <div className="dt-kpi-value" style={{ color }}>
        {value}
      </div>
      <div className="dt-kpi-sub">{sub}</div>
    </div>
  );
}

/** Labelled horizontal bars — label · track · count (pct). One hue per
 *  list (categorical rows are the same measure, not different series). */
function BarList({
  rows,
  total,
  color,
}: {
  rows: { label: string; count: number }[];
  total: number;
  color: string;
}) {
  return (
    <div className="flex flex-col gap-1.5 py-1">
      {rows.map((row) => {
        const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
        return (
          <div
            key={row.label}
            style={{
              display: "grid",
              gridTemplateColumns: "180px 1fr 64px",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                fontSize: "var(--fs-label)",
                color: "var(--text-secondary)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={row.label}
            >
              {row.label}
            </span>
            <div className="dt-bar-bg">
              <div
                className="dt-bar-fill"
                style={{
                  width: `${Math.max(0, Math.min(100, pct))}%`,
                  background: color,
                }}
              />
            </div>
            <span
              style={{
                fontSize: "var(--fs-label)",
                color: "var(--text-muted)",
                fontFamily: "var(--font-mono)",
                textAlign: "right",
              }}
            >
              {row.count} ({pct}%)
            </span>
          </div>
        );
      })}
    </div>
  );
}

function QuestionSection({
  index,
  tally,
  total,
}: {
  index: number;
  tally: QuestionTally;
  total: number;
}) {
  return (
    <div>
      <div className="dt-section-title" style={{ marginTop: 16 }}>
        {index + 1}. {tally.question.label}
      </div>
      {tally.kind === "text" ? (
        <TextAnswers tally={tally} />
      ) : (
        <>
          <BarList
            rows={tally.rows}
            total={total}
            color={
              tally.kind === "scale"
                ? "var(--accent-sky)"
                : "var(--accent-gold)"
            }
          />
          <div
            className="text-xs"
            style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
          >
            {tally.answered} of {total} answered
            {tally.kind === "scale" && tally.average !== null
              ? ` · average ${tally.average}`
              : ""}
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────── Free text + AI grouping ─────────────────── */

type ThemesState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "done"; themes: AnswerTheme[] }
  | { status: "error"; message: string };

function TextAnswers({ tally }: { tally: TextTally }) {
  const [themes, setThemes] = useState<ThemesState>({ status: "idle" });
  const [showRaw, setShowRaw] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Abort an in-flight grouping when the modal unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  async function group(): Promise<void> {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setThemes({ status: "loading" });
    try {
      const result = await clusterAnswers({
        question: tally.question.label,
        answers: tally.answers.map((a) => a.text),
        signal: controller.signal,
      });
      setThemes({ status: "done", themes: result });
    } catch (err) {
      if (controller.signal.aborted) return;
      setThemes({
        status: "error",
        message: err instanceof Error ? err.message : "Grouping failed.",
      });
    }
  }

  const grouped = themes.status === "done";
  const maxCount = grouped
    ? Math.max(1, ...themes.themes.map((t) => t.count))
    : 1;

  if (tally.answers.length === 0) {
    return (
      <div className="py-1 text-xs" style={{ color: "var(--text-muted)" }}>
        No answers yet.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5 py-1">
      <div className="flex items-center gap-2">
        {isClusteringAvailable() && tally.answers.length >= 2 && (
          <button
            type="button"
            onClick={() => void group()}
            disabled={themes.status === "loading"}
            className="modal-close-btn inline-flex items-center gap-1.5"
            style={{ color: "var(--accent-sky)" }}
          >
            <Sparkles aria-hidden style={{ width: 11, height: 11 }} />
            {themes.status === "loading"
              ? "GROUPING…"
              : grouped
                ? "REGROUP"
                : "GROUP SIMILAR ANSWERS"}
          </button>
        )}
        {grouped && (
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="modal-close-btn"
          >
            {showRaw ? "HIDE RAW ANSWERS" : "SHOW RAW ANSWERS"}
          </button>
        )}
      </div>

      {themes.status === "error" && (
        <p role="alert" className="text-xs" style={{ color: "var(--accent-danger)" }}>
          {themes.message}
        </p>
      )}

      {grouped && (
        <div className="flex flex-col gap-1.5">
          {themes.themes.map((theme) => {
            const pct = Math.round((theme.count / maxCount) * 100);
            return (
              <div key={theme.label}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "180px 1fr 40px",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <span
                    style={{ fontSize: "var(--fs-label)", color: "var(--text-secondary)" }}
                    title={theme.label}
                  >
                    {theme.label}
                  </span>
                  <div className="dt-bar-bg">
                    <div
                      className="dt-bar-fill"
                      style={{ width: `${pct}%`, background: "var(--accent-melon)" }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: "var(--fs-label)",
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-mono)",
                      textAlign: "right",
                    }}
                  >
                    {theme.count}
                  </span>
                </div>
                {theme.example && (
                  <div
                    className="pl-1 text-xs italic"
                    style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}
                  >
                    "{truncate(theme.example, 140)}"
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {(!grouped || showRaw) && (
        <ul className="flex flex-col gap-1">
          {tally.answers.map((a, i) => (
            <li
              key={`${a.by}-${i}`}
              className="rounded-md border px-3 py-2 text-xs"
              style={{
                borderColor: "hsl(var(--border) / 0.6)",
                background: "var(--bg-card)",
                color: "var(--text-secondary)",
              }}
            >
              {a.text}
              <span style={{ color: "var(--text-muted)" }}> — {a.by}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ───────────────────── Respondent table ───────────────────── */

function RespondentTable({
  responses,
  questionnaire,
}: {
  responses: readonly QuestionnaireResponse[];
  questionnaire: QuestionnaireContent;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const sorted = useMemo(
    () => [...responses].sort((a, b) => b.submittedAt - a.submittedAt),
    [responses],
  );

  return (
    <div className="flex flex-col gap-1 pt-2">
      {sorted.map((r) => {
        const isOpen = expanded === r.username;
        const Chevron = isOpen ? ChevronDown : ChevronRight;
        return (
          <div
            key={r.username}
            className="rounded-md border"
            style={{
              borderColor: "hsl(var(--border) / 0.6)",
              background: "var(--bg-card)",
            }}
          >
            <button
              type="button"
              onClick={() => setExpanded(isOpen ? null : r.username)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left"
              style={{ cursor: "pointer" }}
              aria-expanded={isOpen}
            >
              <Chevron
                aria-hidden
                style={{ width: 12, height: 12, color: "var(--text-muted)" }}
              />
              <span
                className="text-xs font-semibold"
                style={{ color: "var(--text-primary)" }}
              >
                {r.displayName}
              </span>
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--accent-true-blue)",
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                {r.role || "—"}
              </span>
              <span
                className="ml-auto"
                style={{
                  fontSize: "var(--fs-label)",
                  color: "var(--text-muted)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {r.submittedAt ? new Date(r.submittedAt).toLocaleString() : ""}
              </span>
            </button>
            {isOpen && (
              <div
                className="flex flex-col gap-2 px-3 pt-1 pb-3"
                style={{ borderTop: "1px solid hsl(var(--border) / 0.4)" }}
              >
                {questionnaire.questions.map((q) => {
                  const v = r.answers[q.id];
                  return (
                    <div key={q.id} className="text-xs">
                      <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}>
                        {q.label}
                      </div>
                      <div style={{ color: "var(--text-secondary)" }}>
                        {v === undefined || String(v).trim() === "" ? (
                          <span style={{ color: "var(--text-muted)" }}>
                            — not answered
                          </span>
                        ) : (
                          String(v)
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
